import { useState, type ReactNode } from 'react';
import { validateValues, type Metafield, type MetafieldValue } from '@forecastify/shared';
import { useT } from '../../i18n';
import { MetafieldForm } from './metafield-form';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ One entity's custom fields, as a box on its form.                         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **`metafield_entity` HAS FIVE VALUES AND ONLY TWO OF THEM HAD A FORM.** An admin could
 * define fields for a project, a client or a worklog on the settings screen, and nothing anywhere
 * could fill one in — so those definitions were a list that governed nothing, and the entity picker
 * offered three choices that led nowhere. Task and person had one; the other three now do too.
 *
 * ⚠️ **A shared component rather than three copies.** The draft, the per-field validation and the
 * box are identical for every entity; what differs is the definitions and where the values go.
 * Three copies is three chances for one of them to skip `validateValues`.
 *
 * ⚠️ **It renders NOTHING when the workspace has defined no fields for this entity**, which is the
 * ordinary case — an empty box titled *Metamezők* on every form would be permanent furniture for a
 * feature most workspaces never use.
 */
export function MetafieldSection({
  fields,
  values,
  members,
  disabled,
  onChange,
}: {
  readonly fields: readonly Metafield[];
  readonly values: Readonly<Record<string, unknown>>;
  /** For the `user` type. The form picks a member from this. */
  readonly members: readonly { id: string; name: string }[];
  readonly disabled?: boolean;
  readonly onChange: (values: Record<string, unknown>) => void;
}): ReactNode {
  const t = useT();
  /**
   * ⚠️ **Whether anything has been TOUCHED, not whether it is valid.** An untouched form is not
   * "invalid" — showing a required field's error before anybody has typed makes a new record look
   * broken on arrival. The same rule the task detail's own block applies.
   */
  const [touched, setTouched] = useState(false);

  if (fields.length === 0) return null;

  const specs = fields.map((field) => ({
    key: field.key,
    name: field.name,
    type: field.type,
    required: field.required,
    options: field.options,
  }));
  const errors = touched ? validateValues(specs, values).errors : {};

  return (
    <section className="rounded-xl border p-3">
      <h3 className="mb-2 text-[12px] font-medium">{t('task.metafields')}</h3>
      <MetafieldForm
        fields={fields}
        values={values}
        errors={errors}
        members={members}
        disabled={disabled}
        onChange={(key, value: MetafieldValue) => {
          setTouched(true);
          onChange({ ...values, [key]: value });
        }}
      />
    </section>
  );
}
